import { Grid, Typography } from '@mui/material';
import { Button, RHFAutoCompleteVirtualize } from '@openx/components/core';
import { useCallback } from 'react';
import { getEnvironmentConfigs } from '../../../environmentConfigs';
import type { InstanceListOption } from '../types';
import { useAuth } from './AuthContext';

type InstanceSelectProps = {
  instancesList: InstanceListOption[];
  onButtonClick: (docsUrl: string) => void;
};

export const InstanceSelect = ({ instancesList, onButtonClick }: InstanceSelectProps) => {
  const { currentUser } = useAuth();
  const { env } = getEnvironmentConfigs();

  // With a single instance there is nothing to choose from
  const hideSelect = instancesList.length === 1;

  const docsUrl = env === 'prod' ? 'https://docs.openx.com' : `https://${env}-docs.openx.com`;

  const handleDocsButtonClick = useCallback(() => {
    onButtonClick(docsUrl);
  }, [docsUrl, onButtonClick]);

  if (!currentUser) {
    return null;
  }

  return (
    <>
      <Typography variant="h3" gutterBottom data-test="current-user">
        You are now logged in as {currentUser.email}
      </Typography>

      <Grid
        container
        spacing={3}
        direction="row"
        sx={{ marginTop: 2, marginBottom: 2, alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Grid>
          <RHFAutoCompleteVirtualize
            name="instance"
            textFieldProps={{ label: 'Select instance' }}
            disableClearable
            renderOptions={{
              dataTest: 'instance-item',
            }}
            sx={{ marginBottom: 2, width: 300, display: hideSelect ? 'none' : 'block' }}
            getOptionLabel={option => option.label || ''}
            isOptionEqualToValue={(option, value) => {
              if (typeof value === 'string') {
                return value === option.label;
              }
              return value.id === option.id;
            }}
            options={instancesList}
            data-test="instance-list"
          />
        </Grid>
        <Grid>
          <Button variant="outlined" color="primary" onClick={handleDocsButtonClick} data-test="docs-button">
            See API docs
          </Button>
        </Grid>
      </Grid>
    </>
  );
};
